"use client";
export const dynamic = "force-dynamic";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageCircle,
  Instagram,
  Twitter,
  Linkedin,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { z } from "zod";
import { SiteShell } from "@/components/site/SiteShell";
import PageSkeleton from "@/components/site/PageSkeleton";

const contactSchema = z.object({
  name: z.string().trim().min(2, "نام باید حداقل ۲ حرف باشد").max(100),
  email: z.string().trim().email("ایمیل معتبر وارد کنید").max(255),
  subject: z.string().trim().min(3, "موضوع را وارد کنید").max(150),
  message: z.string().trim().min(10, "پیام حداقل ۱۰ حرف").max(1500),
});

const CHANNELS = [
  {
    icon: Mail,
    label: "ایمیل",
    value: "hello@trendi.smart",
    href: "mailto:hello@trendi.smart",
    color: "text-primary-main bg-primary-main/15",
  },
  {
    icon: Phone,
    label: "تلفن",
    value: "۰۲۱-۹۱۰۰۰۰۰۰",
    href: "tel:+982191000000",
    color: "text-secondary-main bg-secondary-main/15",
  },
  {
    icon: MessageCircle,
    label: "واتساپ",
    value: "پاسخگویی ۲۴/۷",
    href: "https://wa.me/989000000000",
    color: "text-emerald-500 bg-emerald-500/15",
  },
  {
    icon: MapPin,
    label: "دفتر مرکزی",
    value: "تهران، خیابان آزادی",
    href: "#",
    color: "text-purple bg-purple/15",
  },
];

const SOCIALS = [
  { icon: Instagram, href: "#", label: "اینستاگرام" },
  { icon: Twitter, href: "#", label: "توییتر" },
  { icon: Linkedin, href: "#", label: "لینکدین" },
];

export default function ContactPage() {
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle");

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, []);

  if (!ready) return <PageSkeleton variant="contact" />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const errs: Record<string, string> = {};
      result.error.issues.forEach((i) => {
        errs[i.path[0] as string] = i.message;
      });
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    await new Promise((r) => setTimeout(r, 1100));
    setStatus("success");
    setForm({ name: "", email: "", subject: "", message: "" });
    setTimeout(() => setStatus("idle"), 4500);
  };

  const fieldClass = (key: string) =>
    `w-full rounded-xl border bg-background px-4 py-3 text-[13.5px] text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-primary-main focus:ring-2 focus:ring-primary-main/20 ${
      errors[key] ? "border-destructive" : "border-border"
    }`;

  return (
    <SiteShell>
      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pt-16 pb-10 sm:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
            <Sparkles size={14} className="text-primary-main" />
            <span className="text-[12px] font-bold text-muted-foreground">دوست داریم بشنویم</span>
          </div>
          <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            بیایید با هم
            <span className="block brand-gradient-text">صحبت کنیم</span>
          </h1>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            سؤال، پیشنهاد یا فکر بزرگ دارید؟ ما اینجاییم — معمولاً ظرف چند ساعت پاسخ می‌دهیم.
          </p>
        </motion.div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 pb-24 lg:grid-cols-3">
        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:col-span-2 h-fit sticky top-20"
        >
          <h2 className="mb-1 text-xl font-black text-foreground">فرم تماس</h2>
          <p className="mb-6 text-[12.5px] text-muted-foreground">
            فیلدهای زیر را پر کنید — تیم ما با شما تماس می‌گیرد.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[12px] font-bold text-foreground">
                نام و نام خانوادگی
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="مثلاً علی محمدی"
                className={fieldClass("name")}
                maxLength={100}
              />
              {errors.name && <p className="mt-1 text-[11px] text-destructive">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-[12px] font-bold text-foreground">ایمیل</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@example.com"
                className={fieldClass("email")}
                maxLength={255}
                dir="ltr"
              />
              {errors.email && <p className="mt-1 text-[11px] text-destructive">{errors.email}</p>}
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-[12px] font-bold text-foreground">موضوع</label>
            <input
              value={form.subject}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              placeholder="موضوع پیام شما"
              className={fieldClass("subject")}
              maxLength={150}
            />
            {errors.subject && (
              <p className="mt-1 text-[11px] text-destructive">{errors.subject}</p>
            )}
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-[12px] font-bold text-foreground">پیام</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              placeholder="در مورد چه چیزی می‌خواهید صحبت کنیم؟"
              rows={6}
              maxLength={1500}
              className={`${fieldClass("message")} resize-none`}
            />
            <div className="mt-1 flex justify-between">
              {errors.message ? (
                <p className="text-[11px] text-destructive">{errors.message}</p>
              ) : (
                <span />
              )}
              <span className="text-[10.5px] text-muted-foreground">
                {form.message.length}/۱۵۰۰
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-[11.5px] text-muted-foreground">
              با ارسال این فرم، با{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-main hover:underline">
                حریم خصوصی
              </a>{" "}
              ما موافقت می‌کنید.
            </p>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13.5px] font-black text-white shadow-lg shadow-primary-main/30 transition-colors hover:bg-primary-400 disabled:opacity-70 sm:w-auto"
            >
              {status === "submitting" ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> در حال ارسال...
                </>
              ) : status === "success" ? (
                <>
                  <CheckCircle2 size={15} /> ارسال شد
                </>
              ) : (
                <>
                  <Send size={15} /> ارسال پیام
                </>
              )}
            </button>
          </div>

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-[12.5px] text-emerald-600 dark:text-emerald-400"
            >
              <CheckCircle2 size={15} />
              پیام شما با موفقیت ارسال شد. به‌زودی پاسخ می‌دهیم.
            </motion.div>
          )}
        </motion.form>

        {/* Channels */}
        <motion.aside
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-4"
        >
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="block rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary-main/40"
              >
                <div
                  className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${c.color}`}
                >
                  <Icon size={17} />
                </div>
                <h3 className="text-[13px] font-black text-foreground">{c.label}</h3>
                <p
                  className="mt-0.5 text-[12px] text-muted-foreground"
                  dir={c.label === "ایمیل" ? "ltr" : undefined}
                >
                  {c.value}
                </p>
              </a>
            );
          })}

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 text-[13px] font-black text-foreground">شبکه‌های اجتماعی</h3>
            <div className="flex gap-2">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground transition-colors hover:bg-primary-main hover:text-white"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>
        </motion.aside>
      </section>
    </SiteShell>
  );
}
